import TableContainer from 'Components/Common/TableContainerReactTable';
import { formatStringWithUnderscores, getTruncatedId } from 'lib/utils/formatters';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { setSelectedBuilderId, setSelectedPropertyUnitId } from 'slices/builder/builderSlice';

interface ArchivedListingProps {
    data: any[];
    isLoading?: boolean;
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    onRefresh?: () => void;
}

const ArchivedListing = ({
    data,
    currentPage = 1,
    totalPages = 1,
    totalCount = 0,
    onPageChange,
}: ArchivedListingProps) => {
    const navigate = useNavigate();
    const [copiedId, setCopiedId] = React.useState<string | null>(null);

    const customPageSize = 10;
    const dispatch = useDispatch();

    // Function to copy ID to clipboard
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 1 second
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const columns = [
        {
            header: 'No',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const index = (currentPage - 1) * customPageSize + row.index + 1;
                return <span>{index}</span>;
            },
        },
        {
            header: 'Property ID',
            accessorKey: 'id',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const fullId = row.original?.property?.id || row.original?.property_id;
                if (!fullId) return 'N/A';

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                            {getTruncatedId(fullId)}
                        </span>
                        <span
                            onClick={() => copyToClipboard(fullId)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: copiedId === fullId ? '#28a745' : '#6b7280',
                                transition: 'color 0.2s ease'
                            }}
                            title={copiedId === fullId ? 'Copied!' : 'Copy full ID'}
                        >
                            {copiedId === fullId ? (
                                <i className="ri-check-line"></i>
                            ) : (
                                <i className="ri-file-copy-line"></i>
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            header: "Builder", accessorKey: "builder", enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const address = row.original?.builder?.company_name;
                return address || 'N/A';
            },
        },
        {
            header: "Address", accessorKey: "address", enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const address = row.original?.property?.property_address;
                return address || 'N/A';
            }
        },
        {
            header: "Property Type", accessorKey: "propertyType", enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const propertyType = row.original?.property?.property_type;
                return formatStringWithUnderscores(propertyType) || 'N/A';
            }
        },
        {
            header: "Listing Type", accessorKey: "listingType", enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const listingType = row.original?.property?.property_category;
                return formatStringWithUnderscores(listingType) || 'N/A';
            }
        },
        {
            header: 'Status',
            accessorKey: 'status', // yahan data me 'listingStatus' hai
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const status = row?.original?.status;
                const formattedStatus = formatStringWithUnderscores(status);
                switch (status) {
                    case "Pending":
                        return (<span className="badge rounded-pill bg-warning"> {formattedStatus}</span>);
                    case "accepted":
                    case "public":
                    case "payment_pending":
                    case "earnest_paid":
                    case "sold":
                    case "rented":
                        return (<span className="badge rounded-pill bg-success"> {formattedStatus}</span>);
                    case "rejected":
                    case "archived":
                        return (<span className="badge rounded-pill bg-danger"> {formattedStatus}</span>);
                    default:
                        return (<span className="badge rounded-pill  bg-warning"> {formattedStatus}</span>);
                }
            },
        },
        // {
        //     header: 'Company Inspection',
        //     accessorKey: 'companyInspection', // yahan data me 'listingStatus' hai
        //     enableColumnFilter: false,
        //     cell: (cell: any) => {
        //         switch (cell.getValue()) {
        //             case "Pending":
        //                 return (<span className="badge rounded-pill bg-warning text-uppercase "> {cell.getValue()}</span>);
        //             case "Approved":
        //                 return (<span className="badge rounded-pill bg-success text-uppercase "> {cell.getValue()}</span>);
        //             case "Rejected":
        //                 return (<span className="badge rounded-pill bg-danger text-uppercase "> {cell.getValue()}</span>);
        //             default:
        //                 return (<span className="badge rounded-pill  bg-warning text-uppercase"> {cell.getValue()}</span>);
        //         }
        //     },
        // },
        // {
        //     header: 'Secondary Inspection',
        //     accessorKey: 'secondaryInspection', // yahan data me 'listingStatus' hai
        //     enableColumnFilter: false,
        //     cell: (cell: any) => {
        //         switch (cell.getValue()) {
        //             case "Pending":
        //                 return (<span className="badge rounded-pill bg-warning text-uppercase "> {cell.getValue()}</span>);
        //             case "Approved":
        //                 return (<span className="badge rounded-pill bg-success text-uppercase "> {cell.getValue()}</span>);
        //             case "Rejected":
        //                 return (<span className="badge rounded-pill bg-danger text-uppercase "> {cell.getValue()}</span>);
        //             default:
        //                 return (<span className="badge rounded-pill  bg-warning text-uppercase"> {cell.getValue()}</span>);
        //         }
        //     },
        // },
        {
            header: "Action",
            accessorKey: "action",
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => (
                <UncontrolledDropdown direction="start">
                    <DropdownToggle tag="span" style={{ cursor: 'pointer' }}>
                        <i className="ri-more-2-fill" style={{ fontSize: 20, color: '#6b7280' }}></i>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem
                            onClick={() => {
                                // Set both property unit ID and builder ID in Redux
                                const propertyUnitId = row?.original?.id;
                                const builderId = row?.original?.builder?.id;

                                if (propertyUnitId) {
                                    dispatch(setSelectedPropertyUnitId(propertyUnitId));
                                }
                                if (builderId) {
                                    dispatch(setSelectedBuilderId(builderId));
                                }

                                // Pre-set localStorage before navigation to ensure sidebar gets correct value
                                localStorage.setItem('activeMenu', 'properties');

                                navigate('/listing-detail?source=properties');
                            }}
                            className="d-flex align-items-center"
                        >
                            <i className="ri-eye-line me-2"></i> View
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>

            ),
        },
    ];

    return (
        <React.Fragment>
            <TableContainer
                columns={columns}
                data={data}
                customPageSize={customPageSize}
                tableClass="table-centered align-middle table-nowrap mb-0"
                theadClass="text-muted table-light"
                isServerSide={true}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={onPageChange}
            />
        </React.Fragment>
    );
};

export default ArchivedListing;
